import { HttpClient, HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, catchError, firstValueFrom, map, Observable, Subject, tap, throwError } from 'rxjs';

import { environment } from '../../../environments/environment';
import { ApplicationUser } from '../application-user.model';

@Injectable({ providedIn: 'root' })
export class UserAuthorizationService {
    private _user$ = new BehaviorSubject<ApplicationUser | null>(null);
    readonly user$ = this._user$.asObservable();
    readonly loggedIn$ = this.user$.pipe(map(u => !!u));

    constructor(private httpClient: HttpClient) { }

    get user(): ApplicationUser | null { return this._user$.value; }


    async loadUserAsync(): Promise<void> {

        // workaround to not trigger redirect
        let hasActiveSession = await this.hasActiveSession();

        if (!hasActiveSession) return;

        try {
            const raw = await firstValueFrom(
                this.httpClient.get<ApplicationUser>(`${environment.auth}/me`, { withCredentials: true })
            );
            // rehydrate JSON -> class instance so methods like isStaffOrAdmin() exist
            const user = Object.assign(new ApplicationUser(), raw);
            this._user$.next(user);
        } catch (err: any) {
            this._user$.next(null);
        }
    }

    async hasActiveSession(): Promise<boolean> {
        try {
            const res = await fetch(`${environment.auth}/me`, {
                method: 'GET',
                credentials: 'include'
            });

            return res.ok; // true for 200–299
        } catch (err) {
            return false;
        }
    }

    /** New: fetch from backend if not cached */
    async getOrLoadUser(): Promise<ApplicationUser | null> {
        const cached = this._user$.value;
        if (cached) return cached;

        await this.loadUserAsync();
        return this._user$.value;
    }

    async isLoggedIn(): Promise<boolean> {
        return !!(this._user$.value);
    }

    login(returnUrl: string | null = null, rememberLogin: boolean = false): void {
        const redirectUrl = returnUrl ?? `${environment.baseUrl}${window.location.pathname}`;

        const params = new URLSearchParams({
            returnUrl: redirectUrl,
            persistent: String(rememberLogin)
        });

        window.location.href = `${environment.auth}/login?${params.toString()}`;
    }

    loginWithHandoffCode(handOffCode: string): Observable<void> {
        // withCredentials so the auth cookie is stored
        return this.httpClient.post<void>(`${environment.auth}/handoff`,
            { handOffCode },
            { withCredentials: true }
        );
    }

    logout() {
        // immediately tell UI it’s logging out
        this._user$.next(null);

        sessionStorage.clear();
        localStorage.removeItem('oidc.user');
        window.location.href = `${environment.auth}/logout`;
    }

    /** Call if your app detects the session became invalid */
    notifyLoggedOut() { this._user$.next(null); }
}
